import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { AuthGate } from "./components/AuthGate";
import { SubRailLayout, type SubRailItem } from "./components/SubRailLayout";

import Dashboard from "./pages/Dashboard";
import Food from "./pages/Food";
import Notes from "./pages/Notes";
import NutritionToday from "./pages/Nutrition/NutritionToday";
import NutritionHistory from "./pages/Nutrition/NutritionHistory";
import DietToday from "./pages/Diet/DietToday";
import DietTemplate from "./pages/Diet/DietTemplate";
import DietCheckin from "./pages/Diet/DietCheckin";
import DietReport from "./pages/Diet/DietReport";
import DietGuide from "./pages/Diet/DietGuide";
import WeightOverview from "./pages/Weight/WeightOverview";
import WeightTrend from "./pages/Weight/WeightTrend";
import WeightComposition from "./pages/Weight/WeightComposition";
import WeightGoal from "./pages/Weight/WeightGoal";
import Activity from "./pages/Activity";
import HeartOverview from "./pages/Heart/HeartOverview";
import HeartZones from "./pages/Heart/HeartZones";
import HeartResting from "./pages/Heart/HeartResting";
import HeartPeaks from "./pages/Heart/HeartPeaks";
import HeartReport from "./pages/Heart/HeartReport";
import HeartHrv from "./pages/Heart/HeartHrv";
import Sleep from "./pages/Sleep";
import Coach from "./pages/Coach";
import Goals from "./pages/Goals";
import Connections from "./pages/Connections";
import Help from "./pages/Help";

import TrendsWeight from "./pages/Trends/TrendsWeight";
import TrendsNutrition from "./pages/Trends/TrendsNutrition";
import TrendsSleep from "./pages/Trends/TrendsSleep";
import TrendsActivity from "./pages/Trends/TrendsActivity";
import TrendsBody from "./pages/Trends/TrendsBody";

import SettingsProfile from "./pages/Settings/SettingsProfile";
import SettingsCoachTone from "./pages/Settings/SettingsCoachTone";
import SettingsExport from "./pages/Settings/SettingsExport";
import SettingsUsage from "./pages/Settings/SettingsUsage";

const TRENDS_TABS: SubRailItem[] = [
  { to: "/trends/weight", label: "Weight" },
  { to: "/trends/nutrition", label: "Nutrition" },
  { to: "/trends/sleep", label: "Sleep" },
  { to: "/trends/activity", label: "Activity" },
  { to: "/trends/body", label: "Body comp" },
];

const FOOD_TABS: SubRailItem[] = [
  { to: "/food", label: "Log", end: true },
  { to: "/food/today", label: "Today" },
  { to: "/food/history", label: "History" },
];

const DIET_TABS: SubRailItem[] = [
  { to: "/diet", label: "Today", end: true },
  { to: "/diet/template", label: "Template" },
  { to: "/diet/checkin", label: "Check-in" },
  { to: "/diet/report", label: "Report" },
  { to: "/diet/guide", label: "Guide" },
];

const WEIGHT_TABS: SubRailItem[] = [
  { to: "/weight", label: "Overview", end: true },
  { to: "/weight/trend", label: "Trend" },
  { to: "/weight/composition", label: "Composition" },
  { to: "/weight/goal", label: "Goal" },
];

const HEART_TABS: SubRailItem[] = [
  { to: "/heart", label: "Overview", end: true },
  { to: "/heart/zones", label: "Zones" },
  { to: "/heart/resting", label: "Resting" },
  { to: "/heart/peaks", label: "Peaks" },
  { to: "/heart/report", label: "Report" },
  { to: "/heart/hrv", label: "HRV" },
];

const SETTINGS_TABS: SubRailItem[] = [
  { to: "/settings/profile", label: "Profile" },
  { to: "/settings/goals", label: "Goals" },
  { to: "/settings/connections", label: "Connections" },
  { to: "/settings/coach", label: "Coach tone" },
  { to: "/settings/usage", label: "Usage & cost" },
  { to: "/settings/export", label: "Data & export" },
];

export default function App() {
  return (
    <AuthGate>
      <AppRoutes />
    </AuthGate>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/notes" element={<Notes />} />
        <Route path="/food" element={<SubRailLayout title="Food" items={FOOD_TABS} />}>
          <Route index element={<Food />} />
          <Route path="today" element={<NutritionToday />} />
          <Route path="history" element={<NutritionHistory />} />
        </Route>
        <Route path="/activity" element={<Activity />} />
        <Route path="/sleep" element={<Sleep />} />
        <Route path="/coach" element={<Coach />} />
        <Route path="/goals" element={<Goals />} />
        <Route path="/connections" element={<Connections />} />
        <Route path="/help" element={<Help />} />

        <Route path="/trends" element={<SubRailLayout title="Trends" items={TRENDS_TABS} />}>
          <Route index element={<Navigate to="weight" replace />} />
          <Route path="weight" element={<TrendsWeight />} />
          <Route path="nutrition" element={<TrendsNutrition />} />
          <Route path="sleep" element={<TrendsSleep />} />
          <Route path="activity" element={<TrendsActivity />} />
          <Route path="body" element={<TrendsBody />} />
        </Route>

        <Route path="/diet" element={<SubRailLayout title="Diet" items={DIET_TABS} />}>
          <Route index element={<DietToday />} />
          <Route path="template" element={<DietTemplate />} />
          <Route path="checkin" element={<DietCheckin />} />
          <Route path="report" element={<DietReport />} />
          <Route path="guide" element={<DietGuide />} />
        </Route>

        <Route path="/weight" element={<SubRailLayout title="Weight & body" items={WEIGHT_TABS} />}>
          <Route index element={<WeightOverview />} />
          <Route path="trend" element={<WeightTrend />} />
          <Route path="composition" element={<WeightComposition />} />
          <Route path="goal" element={<WeightGoal />} />
        </Route>

        <Route path="/heart" element={<SubRailLayout title="Heart rate" items={HEART_TABS} />}>
          <Route index element={<HeartOverview />} />
          <Route path="zones" element={<HeartZones />} />
          <Route path="resting" element={<HeartResting />} />
          <Route path="peaks" element={<HeartPeaks />} />
          <Route path="report" element={<HeartReport />} />
          <Route path="hrv" element={<HeartHrv />} />
        </Route>

        <Route path="/settings" element={<SubRailLayout title="Settings" items={SETTINGS_TABS} />}>
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<SettingsProfile />} />
          <Route path="goals" element={<Goals />} />
          <Route path="connections" element={<Connections />} />
          <Route path="coach" element={<SettingsCoachTone />} />
          <Route path="usage" element={<SettingsUsage />} />
          <Route path="export" element={<SettingsExport />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
